import { create } from 'zustand';
import Cookies from 'js-cookie';
import { authAPI } from '../services/api';

const TOKEN_KEY = 'cxo_token';
const COOKIE_OPTS = {
  expires: 7, // 7 days
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'Lax',
  path: '/',
};

const useAuthStore = create((set, get) => ({
  user: null,
  loading: false,
  error: null,
  initializing: true, // true until initialize() completes

  // OTP flow state
  pendingEmail: null,
  otpSent: false,
  pendingRole: null, // track which role is registering for redirect

  initialize: async () => {
    const token = Cookies.get(TOKEN_KEY);
    console.log('[Auth] initialize: token exists?', !!token);
    if (!token) {
      set({ initializing: false });
      return;
    }
    try {
      const { data } = await authAPI.me();
      console.log('[Auth] initialize: user loaded:', data.email, data.role);
      set({ user: { ...data, token }, initializing: false });
    } catch (err) {
      console.warn('[Auth] initialize: token invalid, clearing', err.message);
      Cookies.remove(TOKEN_KEY, { path: '/' });
      set({ initializing: false });
    }
  },

  login: async (email, password) => {
    set({ loading: true, error: null });
    console.log('[Auth] login attempt for:', email);
    try {
      const { data } = await authAPI.login({ email, password });
      console.log('[Auth] login success:', data.email, data.role);
      Cookies.set(TOKEN_KEY, data.token, COOKIE_OPTS);
      set({ user: data, loading: false });
      return data;
    } catch (err) {
      const msg = err.response?.data?.error || 'Invalid email or password';
      console.error('[Auth] login failed:', msg, err.response?.status);
      set({ error: msg, loading: false });
      throw err;
    }
  },

  // Step 1: Submit speaker form → sends OTP
  registerSpeaker: async (formData) => {
    set({ loading: true, error: null });
    try {
      const { data } = await authAPI.registerSpeaker(formData);
      console.log('[Auth] registerSpeaker success → otpSent=true, email:', data.email);
      set({ loading: false, pendingEmail: data.email, otpSent: true, pendingRole: 'SPEAKER' });
      return data;
    } catch (err) {
      const msg = err.response?.data?.error
        || (err.response?.data?.fieldErrors && Object.values(err.response.data.fieldErrors).join(', '))
        || 'Registration failed';
      set({ error: msg, loading: false });
      throw err;
    }
  },

  // Step 1: Submit attendee form → sends OTP
  registerAttendee: async (formData) => {
    set({ loading: true, error: null });
    try {
      const { data } = await authAPI.registerAttendee(formData);
      console.log('[Auth] registerAttendee success → otpSent=true, email:', data.email);
      set({ loading: false, pendingEmail: data.email, otpSent: true, pendingRole: 'ATTENDEE' });
      return data;
    } catch (err) {
      const msg = err.response?.data?.error
        || (err.response?.data?.fieldErrors && Object.values(err.response.data.fieldErrors).join(', '))
        || 'Registration failed';
      set({ error: msg, loading: false });
      throw err;
    }
  },

  // Step 2: Verify OTP → creates account, returns JWT
  verifyOtp: async (email, otp) => {
    set({ loading: true, error: null });
    try {
      const { data } = await authAPI.verifyOtp({ email, otp });
      console.log('[Auth] verifyOtp success → user:', data.email, data.role);
      Cookies.set(TOKEN_KEY, data.token, COOKIE_OPTS);
      set({ user: data, loading: false, pendingEmail: null, otpSent: false });
      return data;
    } catch (err) {
      const msg = err.response?.data?.error || 'Invalid verification code';
      set({ error: msg, loading: false });
      throw err;
    }
  },

  // Resend OTP
  resendOtp: async (email) => {
    set({ loading: true, error: null });
    try {
      await authAPI.resendOtp(email);
      set({ loading: false });
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to resend code';
      set({ error: msg, loading: false });
      throw err;
    }
  },

  logout: () => {
    console.log('[Auth] logout — clearing token and page cookies');
    Cookies.remove(TOKEN_KEY, { path: '/' });
    Cookies.remove('cxo_page', { path: '/' });
    set({ user: null, error: null, pendingEmail: null, otpSent: false, pendingRole: null });
  },

  clearError: () => set({ error: null }),
  clearOtpState: () => set({ pendingEmail: null, otpSent: false, pendingRole: null }),
}));

export default useAuthStore;
